import { useRef } from "react";
import {
  Upload, Search, Loader2, Star, Lock, MoreVertical, Download, Share2, History,
  Sparkles, Trash2, Copy, RotateCcw, Pencil, FolderOpen, CheckSquare, Square, X, Home, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { FileGlyph, fmtSize, fmtDate, STATUS_STYLE } from "./util";

function DocRow({ doc, selected, onToggleSelect, onOpen, onAi, onShare, onVersions, onDetail, onFavorite, onCopy, onDelete, onRestore, isTrash }) {
  return (
    <div
      draggable={!isTrash}
      onDragStart={(e) => e.dataTransfer.setData("application/x-fv-doc", doc.doc_id)}
      data-testid={`doc-row-${doc.doc_id}`}
      className={`group grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
        selected ? "border-[#D4AF37]/40 bg-[#151310]" : "border-white/8 bg-[#121214] hover:border-white/15"}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={() => onToggleSelect(doc.doc_id)} data-testid={`select-doc-${doc.doc_id}`}
          className="text-[#71717A] hover:text-[#D4AF37]">
          {selected ? <CheckSquare className="w-4 h-4 text-[#D4AF37]" /> : <Square className="w-4 h-4" />}
        </button>
        <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] shrink-0">
          <FileGlyph filename={doc.filename} className="w-4.5 h-4.5" />
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <button onClick={() => onOpen(doc.doc_id)} className="text-sm text-white font-medium truncate hover:text-[#D4AF37]" title={doc.filename} data-testid={`open-doc-${doc.doc_id}`}>{doc.filename}</button>
          {doc.favorite && <Star className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" fill="currentColor" />}
          {doc.locked && <Lock className="w-3.5 h-3.5 text-[#71717A] shrink-0" />}
          {doc.version > 1 && <span className="text-[9px] text-[#A1A1AA] border border-white/15 rounded-full px-1.5">v{doc.version}</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-[#71717A]">
          <span className={`px-1.5 py-0.5 rounded-full ${STATUS_STYLE[doc.status] || "bg-white/8 text-[#A1A1AA]"}`}>{doc.status}</span>
          <span>{doc.category}</span><span>·</span><span>{fmtSize(doc.size)}</span><span>·</span><span>{fmtDate(doc.updated_at)}</span>
          {doc.tags?.slice(0, 3).map((t) => <span key={t} className="text-[#D4AF37]/80">#{t}</span>)}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isTrash ? (
          <>
            <button onClick={() => onRestore(doc.doc_id)} data-testid={`restore-doc-${doc.doc_id}`} title="Restore" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-emerald-400 hover:bg-white/5 flex items-center justify-center"><RotateCcw className="w-4 h-4" /></button>
            <button onClick={() => onDelete(doc.doc_id, true)} data-testid={`purge-doc-${doc.doc_id}`} title="Delete forever" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-red-400 hover:bg-white/5 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
          </>
        ) : (
          <>
            <button onClick={() => onAi(doc)} data-testid={`ai-doc-${doc.doc_id}`} title="AI Assistant" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-[#D4AF37] hover:bg-white/5 flex items-center justify-center"><Sparkles className="w-4 h-4" /></button>
            <button onClick={() => onFavorite(doc)} data-testid={`fav-doc-${doc.doc_id}`} title={doc.favorite ? "Unfavorite" : "Favorite"} className={`w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center ${doc.favorite ? "text-[#D4AF37]" : "text-[#71717A] hover:text-[#D4AF37]"}`}><Star className="w-4 h-4" fill={doc.favorite ? "currentColor" : "none"} /></button>
            <button onClick={() => onOpen(doc.doc_id)} data-testid={`download-${doc.doc_id}`} title="Open / download" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-white hover:bg-white/5 flex items-center justify-center"><Download className="w-4 h-4" /></button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild data-testid={`doc-menu-${doc.doc_id}`}>
                <button className="w-8 h-8 rounded-lg text-[#71717A] hover:text-white hover:bg-white/5 flex items-center justify-center"><MoreVertical className="w-4 h-4" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1C1C1F] border-white/10 text-white">
                <DropdownMenuItem onClick={() => onDetail(doc)} data-testid={`edit-doc-${doc.doc_id}`} className="focus:bg-white/10 text-sm"><Pencil className="w-4 h-4 mr-2" /> Details & edit</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onFavorite(doc)} className="focus:bg-white/10 text-sm"><Star className="w-4 h-4 mr-2" /> {doc.favorite ? "Unfavorite" : "Favorite"}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onVersions(doc)} data-testid={`versions-doc-${doc.doc_id}`} className="focus:bg-white/10 text-sm"><History className="w-4 h-4 mr-2" /> Version history</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onShare(doc)} data-testid={`share-doc-${doc.doc_id}`} className="focus:bg-white/10 text-sm"><Share2 className="w-4 h-4 mr-2" /> Share link</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onCopy(doc.doc_id)} data-testid={`copy-doc-${doc.doc_id}`} className="focus:bg-white/10 text-sm"><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => onDelete(doc.doc_id, false)} data-testid={`delete-doc-${doc.doc_id}`} className="focus:bg-red-500/15 text-red-400 text-sm"><Trash2 className="w-4 h-4 mr-2" /> Move to trash</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
}

export default function DocGrid({
  docs, meta, view, breadcrumb, selected, bulkActions, filters, setFilters,
  uploading, onUpload, onClearFolder, onCrumb, rowHandlers, onSelectAll, onClearSelect,
}) {
  const fileRef = useRef(null);
  const categories = meta?.categories || [];
  const statuses = meta?.statuses || [];
  const isTrash = view === "trash";
  const selCount = selected.size;

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="flex-1 min-w-0">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="docs-search"
            placeholder="Search documents, tags, notes…"
            className="w-full bg-[#121214] border border-white/10 rounded-full pl-9 pr-4 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-36 h-9 bg-[#121214] border-white/10 text-white text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-[#1C1C1F] border-white/10 text-white max-h-72">
              <SelectItem value="all" className="focus:bg-white/10">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c} className="focus:bg-white/10">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="w-28 h-9 bg-[#121214] border-white/10 text-white text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">
              <SelectItem value="all" className="focus:bg-white/10">Any status</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.sort} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}>
            <SelectTrigger className="w-28 h-9 bg-[#121214] border-white/10 text-white text-xs" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">
              <SelectItem value="recent" className="focus:bg-white/10">Recent</SelectItem>
              <SelectItem value="name" className="focus:bg-white/10">Name</SelectItem>
              <SelectItem value="size" className="focus:bg-white/10">Size</SelectItem>
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" onChange={onFileChange} className="hidden" data-testid="file-input"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.csv,.txt,.md,.doc,.docx,.xlsx,.pptx" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90 h-9">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}
          </Button>
        </div>
      </div>

      {/* Breadcrumb */}
      {view === "folder" && (
        <div className="flex items-center gap-1 text-xs text-[#71717A] mb-3" data-testid="breadcrumb">
          <button onClick={onClearFolder} className="hover:text-white flex items-center gap-1"><Home className="w-3.5 h-3.5" /> All</button>
          {breadcrumb.map((f) => (
            <span key={f.folder_id} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              <button onClick={() => onCrumb(f.folder_id)} className="hover:text-white">{f.name}</button>
            </span>
          ))}
        </div>
      )}

      {/* Bulk bar */}
      {selCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3 p-2 rounded-xl border border-[#D4AF37]/30 bg-[#151310]" data-testid="bulk-bar">
          <span className="text-xs text-white px-2">{selCount} selected</span>
          <button onClick={onSelectAll} className="text-xs text-[#A1A1AA] hover:text-white px-2">Select all</button>
          {isTrash ? (
            <>
              <Button size="sm" onClick={() => bulkActions("restore")} data-testid="bulk-restore" className="h-7 text-xs bg-white/5 hover:bg-white/10 rounded-full">Restore</Button>
              <Button size="sm" onClick={() => bulkActions("delete")} data-testid="bulk-purge" className="h-7 text-xs bg-red-500/15 text-red-300 hover:bg-red-500/25 rounded-full">Delete forever</Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => bulkActions("favorite")} data-testid="bulk-favorite" className="h-7 text-xs bg-white/5 hover:bg-white/10 rounded-full">Favorite</Button>
              <Button size="sm" onClick={() => bulkActions("archive")} data-testid="bulk-archive" className="h-7 text-xs bg-white/5 hover:bg-white/10 rounded-full">Archive</Button>
              <Button size="sm" onClick={() => bulkActions("move")} data-testid="bulk-move" className="h-7 text-xs bg-white/5 hover:bg-white/10 rounded-full">Move to root</Button>
              <Button size="sm" onClick={() => bulkActions("delete")} data-testid="bulk-delete" className="h-7 text-xs bg-red-500/15 text-red-300 hover:bg-red-500/25 rounded-full">Trash</Button>
            </>
          )}
          <button onClick={onClearSelect} className="ml-auto text-[#71717A] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* List */}
      {docs.length === 0 ? (
        <div className="text-center py-24 rounded-2xl border border-dashed border-white/10 bg-[#0f0f11]" data-testid="docs-empty">
          <FolderOpen className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
          <p className="text-[#71717A]">{isTrash ? "Trash is empty." : "No documents here yet. Upload a file to get started."}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="docs-list">
          {docs.map((d) => (
            <DocRow key={d.doc_id} doc={d} selected={selected.has(d.doc_id)} isTrash={isTrash} {...rowHandlers} />
          ))}
        </div>
      )}
    </div>
  );
}
